<template>
  <div class="cell-container">
    <slot name="cell" v-bind="cellAttrs()" />
    <!-- v-if is required, otherwise they might not be cleanly removed fom the DOM, breaking tests -->
    <v-menu
      v-if="isEditing"
      v-model="isEditing"
      :nudge-left="width / 2"
      :close-on-content-click="false"
      :position-x="x"
      :position-y="y"
      absolute
      top
      offset-y
    >
      <bm-card data-qa="grid_cell_edit_dialog">
        <bm-form ref="form" @submit="submit">
          <v-container fluid pb-2>
            <div>{{ editRendererMetadata.editDialogTitle }}</div>
            <slot name="control" v-bind="controlAttrs()"> </slot>
          </v-container>
        </bm-form>

        <template #controls>
          <v-col cols="6" class="text-left pa-0">
            <bm-btn
              data-qa="cancel"
              icon
              x-large
              button-type="ghost"
              @click="cancel"
            >
              <bm-icon icon="Cancel" />
            </bm-btn>
          </v-col>
          <v-col cols="6" class="text-right pa-0">
            <bm-btn
              data-qa="accept"
              icon
              x-large
              button-type="ghost"
              @click="submit"
            >
              <bm-icon icon="Accept" />
            </bm-btn>
          </v-col>
        </template>
      </bm-card>
    </v-menu>
  </div>
</template>

<script setup lang="ts">
import BmBtn from "@/components/controls/input/BmBtn.vue";
import BmCard from "@/components/controls/card/BmCard.vue";
import type { EditableRendererMetadata } from "@/components/controls/grid/renderers/editors/types";
import type { Renderable } from "@/components/controls/grid/BmGridData";
import {
  prefix,
  suffix,
} from "@/components/controls/grid/renderers/rendererUtilities";
import { computed, nextTick, watch } from "vue";
import { makeRef } from "@/components/composables/reactivityTypes";
import type { BmFormInterface } from "@/components/controls/BmFormTypes";

interface DataCellEditDialogProps {
  item?: unknown;
  value?: unknown;
  context?: Renderable;
}

type BmFormRef = BmFormInterface & { focus(): void };

const props = defineProps<DataCellEditDialogProps>();

const form = makeRef<BmFormRef | null>(null);
const isEditing = makeRef(false);
const internalValue = makeRef<any>(null);
const x = makeRef(0);
const y = makeRef(0);

const editRendererMetadata = computed(
  (): EditableRendererMetadata => props.context!.rendererMetadata.editRenderer
);

const width = computed(
  (): number => editRendererMetadata.value.dialogWidth || 200
);

watch(isEditing, (editing) => {
  if (editing) {
    syncInternalValueFromItem(props.item);
    focusOnFormWhenReady();
  }
});

watch(() => props.item, syncInternalValueFromItem, { deep: true });

function cellAttrs(): any {
  if (editRendererMetadata.value.rowEditable(props.item)) {
    return {
      props: {
        ...props,
        onClicked: (event: MouseEvent) => {
          x.value = event.clientX;
          y.value = event.clientY - 5;

          isEditing.value = true;
        },
      },
    };
  } else {
    return {
      props,
    };
  }
}

function setValue(val: any): void {
  internalValue.value = val;
}

function controlAttrs(): any {
  const controlAttrs = {
    props: {
      value: internalValue.value,
      prefix: prefix(props.context!, props.item, internalValue.value),
      suffix: suffix(props.context!, props.item, internalValue.value),
    },
    setValue,
  };

  Object.assign(
    controlAttrs.props,
    editRendererMetadata.value.editControlProps instanceof Function
      ? editRendererMetadata.value.editControlProps(props.item)
      : editRendererMetadata.value.editControlProps
  );

  return controlAttrs;
}

function focusOnFormWhenReady(depth: number = 0): void {
  // Have to wait for the form element to be created, which may take a few ticks depending on depth
  if (depth > 10) {
    // prevent infinite recursion
    return;
  }
  if (form.value == undefined) {
    nextTick(() => focusOnFormWhenReady(depth + 1));
  } else {
    form.value.focus();
  }
}

function syncInternalValueFromItem(newItem: unknown) {
  internalValue.value = editRendererMetadata.value.editItemValue(newItem);
}

function validate(): boolean {
  return form.value!.validate();
}

function cancel() {
  isEditing.value = false;
}

function submit(event: Event) {
  // Prevent the page from refreshing as some users will press enter
  event.preventDefault();

  if (validate()) {
    editRendererMetadata.value.editInserter(props.item, internalValue.value);
    isEditing.value = false;
  }
}
</script>

<style scoped lang="sass">
@import "src/sass/typography"
@import "src/sass/colours"

// Collapse all the padding in the card
:deep(.v-card__text)
  padding: 0 !important

:deep(.v-card__actions)
  padding: 0 !important

.cell-container
  width: 100%
</style>
